import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { formatYds } from "../../utility/installerCapacityRemaining";

// #150 (2026-05-21) — same FileMaker-protocol invoice link used in
// JobsDetailModal (`fmp://$/KellerInteriors.fmp12?script=PresentInvoice_
// (_invoiceNo_)&param=...`). Clicking the invoice# on a recommendation
// row opens the invoice in FileMaker (handled by the OS via the fmp://
// scheme). Mirrors JobsDetailModal.getInvoiceHref so a future FileMaker
// script-name change is a one-place edit; we should extract this to a
// shared utility if it diverges again. Click also stops the popover's
// outside-click handler from collapsing the popover.
export const getInvoiceHref = (invoiceNo) =>
  `fmp://$/KellerInteriors.fmp12?script=PresentInvoice_(_invoiceNo_)&param=${invoiceNo}`;

// - - - - - - - - - -
// Two-section popover that renders the output of
// `recommendFillerCarpetJobs` for issue #140. Anchored to the clicked
// PartialCapacityIcon; closes on outside-click, Escape, or close-button.
//
// Visual structure:
//
//   ┌─────────────────────────────────────────────┐
//   │  Partial Carpet Capacity                  ✕ │  header band
//   │  <installer name> · <cellDate>              │
//   │  <remaining> yds remaining of <effectiveCap>│
//   │  (<N> carpet jobs · <U> yds used)           │
//   ├─────────────────────────────────────────────┤
//   │  🏃🏽‍♂️ Expedite — pull-forward candidates    │  section 1 label
//   │  ────                                       │
//   │  <row> <row> <row>                          │
//   ├─────────────────────────────────────────────┤
//   │  🔴 RTS — unassigned candidates             │  section 2 label
//   │  ────                                       │
//   │  <row> <row>                                │
//   └─────────────────────────────────────────────┘
//
// Read-only v1 — clicking a row does NOT initiate a move; the install
// manager closes the popover and re-assigns manually via drag-drop.
// Click-to-action is a documented v1.1 scope expansion.

const POPOVER_WIDTH = 420;
const POPOVER_MAX_HEIGHT = 480;

// - - - - - - - - - -
// Position the popover relative to the clicked icon's bounding rect.
// Default: anchored below the icon, right-aligned. Clamps to viewport so
// the popover never renders off-screen at edges of the grid.
const computePopoverPosition = (anchorRect, viewportWidth, viewportHeight) => {
  if (!anchorRect) return { left: 0, top: 0 };
  // Prefer below-and-aligned-right of the icon; flip above if not enough room.
  let left = anchorRect.right - POPOVER_WIDTH;
  let top = anchorRect.bottom + 4;
  // Right-edge clamp
  if (left + POPOVER_WIDTH > viewportWidth - 8) {
    left = viewportWidth - POPOVER_WIDTH - 8;
  }
  // Left-edge clamp (icons near the start-of-grid)
  if (left < 8) left = 8;
  // Bottom-edge flip
  if (top + POPOVER_MAX_HEIGHT > viewportHeight - 8) {
    const flipped = anchorRect.top - POPOVER_MAX_HEIGHT - 4;
    if (flipped >= 8) {
      top = flipped;
    } else {
      // Both directions too tight — anchor to top of viewport with vertical
      // scroll fallback. Better to be visible-with-scroll than off-screen.
      top = 8;
    }
  }
  return { left, top };
};

// Row renderer — shared between the two tier sections to keep their visual
// language identical. The tier-specific badge is rendered by the section
// wrapper, not here.
const FillerRow = ({ entry }) => {
  const customer =
    `${entry.customerFirstName} ${entry.customerLastName}`.trim() ||
    "(no name)";
  const isExpedite = entry.expedite;
  // Furniture / steps piece counts are added 2026-05-19 (UAT review #2).
  // Only show the badge when the job actually contains pieces of that type
  // (e.g. don't render "0 furniture" — it's just noise for furniture-free
  // jobs, which is most of them).
  const furniturePieces = entry.furniturePieces || 0;
  const stepPieces = entry.stepPieces || 0;
  return (
    <div
      data-testid="filler-row"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        padding: "8px 12px",
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "8px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
          {/* #150 — clickable invoice# opens the invoice in FileMaker
              via the same fmp:// link used in JobsDetailModal. Click
              propagation must NOT bubble to the popover's outside-click
              handler. target="_blank" + rel="noreferrer" mirror the
              JobsDetailModal pattern. */}
          <a
            href={getInvoiceHref(entry.invoiceNo)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "inherit",
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              cursor: "pointer",
            }}
          >
            #{entry.invoiceNo}
          </a>{" "}
          · {customer}
        </span>
        <span style={{ fontWeight: 700, fontSize: "0.875rem", whiteSpace: "nowrap" }}>
          {formatYds(entry.carpetYds)} yds
          {furniturePieces > 0 && (
            <span style={{ marginLeft: "8px", opacity: 0.7, fontWeight: 600 }}>
              · 🪑 {formatYds(furniturePieces)}
            </span>
          )}
          {stepPieces > 0 && (
            <span style={{ marginLeft: "8px", opacity: 0.7, fontWeight: 600 }}>
              · 🪜 {formatYds(stepPieces)}
            </span>
          )}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: "10px",
          fontSize: "0.75rem",
          opacity: 0.75,
          flexWrap: "wrap",
        }}
      >
        <span>📍 {entry.location || "—"}</span>
        {entry.storeNo && <span>🏪 Store {entry.storeNo}</span>}
        {isExpedite && entry.dateScheduled && (
          <span>📅 currently {entry.dateScheduled}</span>
        )}
        {isExpedite && entry.installer && (
          <span>👤 {entry.installer}</span>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ icon, label, count }) => (
  <div
    style={{
      padding: "8px 12px 4px 12px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      opacity: 0.85,
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    }}
  >
    <span style={{ marginRight: "6px" }}>{icon}</span>
    {label}
    <span style={{ marginLeft: "6px", opacity: 0.7 }}>({count})</span>
  </div>
);

const EmptyTierMessage = ({ message }) => (
  <div
    style={{
      padding: "8px 12px 12px 12px",
      fontSize: "0.8rem",
      opacity: 0.6,
      fontStyle: "italic",
    }}
  >
    {message}
  </div>
);

export const RecommendedFillersPopover = ({
  open,
  anchorRect,
  onClose,
  installerName,
  cellDate,
  capacityInfo,
  recommendations,
}) => {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const expediteJobs = recommendations?.expediteJobs || [];
  const rtsJobs = recommendations?.rtsJobs || [];
  const alreadyScheduledJobs = recommendations?.alreadyScheduledJobs || [];
  const repairJobs = recommendations?.repairJobs || [];

  const { left, top } = computePopoverPosition(
    anchorRect,
    window.innerWidth,
    window.innerHeight
  );

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Recommended filler jobs"
      data-testid="recommended-fillers-popover"
      style={{
        position: "fixed",
        left,
        top,
        width: `${POPOVER_WIDTH}px`,
        maxHeight: `${POPOVER_MAX_HEIGHT}px`,
        zIndex: 9999,
        backgroundColor: "rgb(var(--color-base))",
        color: "rgb(var(--color-text-primary))",
        border: "1px solid rgba(89, 89, 89, 0.4)",
        borderRadius: "6px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // Counter-rotate the OptimizerGrid's red-tint inset on the parent
        // cell — without this the popover inherits the red wash via opacity
        // overlays. createPortal anchors us to document.body so we're outside
        // the cell anyway, but the z-index makes us a fresh stacking context.
      }}
    >
      {/* Header band */}
      <div
        style={{
          padding: "10px 12px 10px 12px",
          borderBottom: "1px solid rgba(89, 89, 89, 0.3)",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>
            Partial Carpet Capacity
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="recommended-fillers-popover-close"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "1rem",
              padding: "2px 6px",
              opacity: 0.65,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: "0.8rem", opacity: 0.85 }}>
          {installerName || "(installer)"} · {cellDate}
        </div>
        {capacityInfo && (
          <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
            {/*
              #167 (2026-05-29): SUPERSEDES the #157 framing. Under the new
              per-stop cap + daily-budget model, `remaining` is the daily
              budget left over (safeTwice × 2 − usedYds in twice-mode).
              The "Next job: up to X yds" copy still reads correctly because
              a single recommended job CAN be up to that remaining budget —
              the installer absorbs the per-stop split flexibly. Used-yds +
              job count still shown for context.
            */}
            <div>
              Next job: up to {formatYds(capacityInfo.remaining)} yds ·{" "}
              {capacityInfo.carpetJobCount} carpet job
              {capacityInfo.carpetJobCount === 1 ? "" : "s"} ·{" "}
              {formatYds(capacityInfo.usedYds)} yds used
            </div>
            {capacityInfo.effectiveFurnitureCap > 0 && (
              <div>
                🪑 Next job: up to {formatYds(capacityInfo.remainingFurniture)}{" "}
                furniture · {formatYds(capacityInfo.usedFurniture)} used
              </div>
            )}
            {capacityInfo.effectiveStepsCap > 0 && (
              <div>
                🪜 Next job: up to {formatYds(capacityInfo.remainingSteps)}{" "}
                steps · {formatYds(capacityInfo.usedSteps)} used
              </div>
            )}
            {/* #146 — stops hard caps. Render only when the cap is
                configured (Brandon-populated value present on the doc),
                independent of the value itself — so a configured "0" still
                surfaces visibly. */}
            {capacityInfo.isStopsJobsCapConfigured && (
              <div>
                📋 {formatYds(capacityInfo.remainingJobStops)} job stops
                remaining of {formatYds(capacityInfo.effectiveStopsJobsCap)} ·{" "}
                {formatYds(capacityInfo.usedJobStops)} used
              </div>
            )}
            {capacityInfo.isStopsRepairsCapConfigured && (
              <div>
                🔧 {formatYds(capacityInfo.remainingRepairStops)} repair stops
                remaining of {formatYds(capacityInfo.effectiveStopsRepairsCap)}{" "}
                · {formatYds(capacityInfo.usedRepairStops)} used
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {/* Tier 1 — Expedite */}
        <SectionHeader
          icon="🏃🏽‍♂️"
          label="Expedite — pull-forward candidates"
          count={expediteJobs.length}
        />
        {expediteJobs.length === 0 ? (
          <EmptyTierMessage message="No future expedite-flagged carpet jobs at this location that fit the remaining capacity." />
        ) : (
          expediteJobs.map((entry) => (
            <FillerRow key={`exp-${entry._id || entry.invoiceNo}`} entry={entry} />
          ))
        )}

        {/* Tier 2 — RTS */}
        <SectionHeader
          icon="🔴"
          label="RTS — unassigned candidates"
          count={rtsJobs.length}
        />
        {rtsJobs.length === 0 ? (
          <EmptyTierMessage message="No RTS carpet jobs at this installer's eligible locations that fit the remaining capacity." />
        ) : (
          rtsJobs.map((entry) => (
            <FillerRow key={`rts-${entry._id || entry.invoiceNo}`} entry={entry} />
          ))
        )}

        {/* Tier 4 — Already Scheduled Pull Forward (issue #156, 2026-05-22).
            Non-expedite carpet Jobs at active statuses (1.x, 2.x, 4) — but
            NOT 3 (= Tier 2's domain) — with a future date_scheduled. Use
            case: FAV auto-pre-scheduled docs at status 1.2 with tentative
            date and unassigned installer, ready to be claimed when material
            is in. */}
        <SectionHeader
          icon="📅"
          label="Already Scheduled — pull-forward candidates"
          count={alreadyScheduledJobs.length}
        />
        {alreadyScheduledJobs.length === 0 ? (
          <EmptyTierMessage message="No future-dated non-expedite carpet jobs that fit the remaining capacity." />
        ) : (
          alreadyScheduledJobs.map((entry) => (
            <FillerRow
              key={`asc-${entry._id || entry.invoiceNo}`}
              entry={entry}
            />
          ))
        )}

        {/* Tier 3 — Open Repairs (issue #145, 2026-05-20). Repair Orders
            assigned to this installer that are still open (statusCode 30).
            Synthetic carpetYds = 1; always fits when remaining > 0. */}
        <SectionHeader
          icon="🔧"
          label="Repairs — open repair orders"
          count={repairJobs.length}
        />
        {repairJobs.length === 0 ? (
          <EmptyTierMessage message="No open repair orders assigned to this installer." />
        ) : (
          repairJobs.map((entry) => (
            <FillerRow key={`rep-${entry._id || entry.invoiceNo}`} entry={entry} />
          ))
        )}
      </div>
    </div>,
    document.body
  );
};

// Exported for direct testing — pure helper, no React.
export { computePopoverPosition };

export default RecommendedFillersPopover;
